import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  TrophyIcon,
  CalendarDaysIcon,
  DocumentArrowDownIcon,
  ShareIcon,
  CheckCircleIcon,
  StarIcon,
  ArrowLeftIcon,
  PrinterIcon,
  LinkIcon
} from '@heroicons/react/24/outline';

const CertificateViewer = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);

  // Mock certificate data - in real app this would come from API
  const certificateData = useMemo(() => ({
    id: certificateId,
    courseName: 'Advanced Healthcare Management',
    studentName: 'Dr. John Smith',
    instructorName: 'Dr. Sarah Wilson',
    issueDate: '2025-01-10',
    completionDate: '2025-01-10',
    grade: 95,
    certificateNumber: 'AHM-2025-001234',
    issuingAuthority: 'Apollo Healthcare Academy',
    courseDuration: '8 weeks',
    totalHours: 12,
    skills: [
      'Strategic Planning',
      'Quality Management', 
      'Healthcare Leadership',
      'Risk Assessment',
      'Change Management',
      'Financial Planning'
    ],
    validUntil: '2027-01-10',
    ceuCredits: 24,
    qrCode: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    verificationUrl: `https://certificates.apollo-health.com/verify/${certificateId}`
  }), [certificateId]);

  useEffect(() => {
    // Simulate loading certificate data
    setTimeout(() => {
      setCertificate(certificateData);
      setIsLoading(false);
    }, 1000);
  }, [certificateId, certificateData]);

  const handleDownload = () => {
    // In real app, this would generate and download a PDF
    const link = document.createElement('a');
    link.href = '#'; // Would be actual PDF URL
    link.download = `Certificate_${certificate.courseName.replace(/\s+/g, '_')}.pdf`;
    link.click();
  };

  const handlePrint = () => {
    window.print();
  };

  const copyVerificationLink = () => {
    navigator.clipboard.writeText(certificate.verificationUrl);
    // Show toast notification in real app
  };

  const shareOnLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(certificate.courseName)}&organizationName=${encodeURIComponent(certificate.issuingAuthority)}&issueYear=${new Date(certificate.issueDate).getFullYear()}&issueMonth=${new Date(certificate.issueDate).getMonth() + 1}&certUrl=${encodeURIComponent(certificate.verificationUrl)}`;
    window.open(linkedInUrl, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-accent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <TrophyIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Certificate not found</h3>
            <p className="text-gray-600 mb-6">
              The certificate you're looking for doesn't exist or may have been removed.
            </p>
            <button
              onClick={() => navigate('/training/my-training')}
              className="bg-apollo-accent text-white px-6 py-3 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200"
            >
              Back to My Training
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div className="flex items-start sm:items-center">
            <button 
              onClick={() => navigate('/training/my-training')}
              className="mr-3 sm:mr-4 p-2 text-gray-600 hover:text-apollo-accent transition-colors duration-200 mt-1 sm:mt-0"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center flex-wrap">
                <TrophyIcon className="h-6 w-6 sm:h-8 sm:w-8 text-apollo-accent mr-2 sm:mr-3" />
                <span>Certificate of Completion</span>
              </h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
                {certificate.courseName}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={handlePrint}
              className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center text-sm"
            >
              <PrinterIcon className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Print</span>
            </button>
            
            <button
              onClick={handleDownload}
              className="bg-apollo-accent text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 flex items-center text-sm"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Download PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Certificate Display */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden print:shadow-none print:border-none">
              {/* Certificate Header */}
              <div className="bg-gradient-to-r from-apollo-accent to-blue-600 p-4 sm:p-6 lg:p-8 text-white">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-3 sm:mb-4">
                    <TrophyIcon className="h-12 w-12 sm:h-16 sm:w-16 text-yellow-300" />
                  </div>
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">Certificate of Completion</h2>
                  <div className="w-16 sm:w-24 h-1 bg-yellow-300 mx-auto rounded-full"></div>
                </div>
              </div>

              {/* Certificate Body */}
              <div className="p-6 sm:p-8 lg:p-12">
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-4 sm:mb-6">
                    This is to certify that
                  </p>
                  
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 border-b-2 border-apollo-accent pb-2 sm:pb-4 inline-block">
                    {certificate.studentName}
                  </h3>
                  
                  <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-3 sm:mb-4">
                    has successfully completed the course
                  </p>
                  
                  <h4 className="text-lg sm:text-xl lg:text-2xl font-semibold text-apollo-accent mb-6 sm:mb-8">
                    {certificate.courseName}
                  </h4>
                </div>

                {/* Course Details */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8 text-center">
                  <div>
                    <div className="flex items-center justify-center mb-2">
                      <CalendarDaysIcon className="h-5 w-5 sm:h-6 sm:w-6 text-apollo-accent mr-2" />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">Completion Date</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">
                      {new Date(certificate.completionDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-center mb-2">
                      <StarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-500 mr-2" />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">Final Grade</p>
                    <p className="font-semibold text-green-600 text-lg sm:text-xl">{certificate.grade}%</p>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-center mb-2">
                      <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-2" />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">CEU Credits</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{certificate.ceuCredits}</p>
                  </div>
                </div>

                {/* Skills Earned */}
                <div className="mb-6 sm:mb-8">
                  <h5 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 text-center">
                    Skills Demonstrated
                  </h5>
                  <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                    {certificate.skills.map((skill, index) => (
                      <span 
                        key={index} 
                        className="bg-apollo-accent/10 text-apollo-accent px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Signature Section */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between pt-6 sm:pt-8 border-t border-gray-200 space-y-6 sm:space-y-0">
                  <div className="text-center">
                    <div className="w-32 sm:w-48 border-b border-gray-400 mb-2 mx-auto"></div>
                    <p className="text-xs sm:text-sm text-gray-600">Instructor</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{certificate.instructorName}</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gray-100 rounded-lg flex items-center justify-center mb-2 mx-auto">
                      <img 
                        src={certificate.qrCode} 
                        alt="QR Code for verification" 
                        className="w-20 h-20 sm:w-24 sm:h-24"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{ display: 'none' }} className="text-xs text-gray-500">
                        QR Code
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">Scan to verify</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-32 sm:w-48 border-b border-gray-400 mb-2 mx-auto"></div>
                    <p className="text-xs sm:text-sm text-gray-600">Issuing Authority</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{certificate.issuingAuthority}</p>
                  </div>
                </div>

                {/* Certificate Footer */}
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-100 text-center">
                  <p className="text-xs sm:text-sm text-gray-500 px-2">
                    <span className="block sm:inline">Certificate Number: {certificate.certificateNumber}</span>
                    <span className="hidden sm:inline"> • </span>
                    <span className="block sm:inline">Issue Date: {new Date(certificate.issueDate).toLocaleDateString()}</span>
                    <span className="hidden sm:inline"> • </span>
                    <span className="block sm:inline">Valid Until: {new Date(certificate.validUntil).toLocaleDateString()}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6 print:hidden">
            {/* Certificate Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Certificate Details</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Certificate ID:</span>
                  <span className="font-medium text-gray-900 text-right">{certificate.certificateNumber}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Course Duration:</span>
                  <span className="font-medium text-gray-900">{certificate.courseDuration}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Hours:</span>
                  <span className="font-medium text-gray-900">{certificate.totalHours}h</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Issue Date:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(certificate.issueDate).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Valid Until:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(certificate.validUntil).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">CEU Credits:</span>
                  <span className="font-medium text-gray-900">{certificate.ceuCredits}</span>
                </div>
              </div>
            </div>

            {/* Verification */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification</h3>
              
              <p className="text-sm text-gray-600 mb-4">
                Share this certificate or verify its authenticity using the verification link below.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={copyVerificationLink}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center text-sm"
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Copy Verification Link
                </button>
                
                <button
                  onClick={shareOnLinkedIn}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  <ShareIcon className="h-4 w-4 mr-2" />
                  Share on LinkedIn
                </button>
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Verified Certificate</p>
                    <p className="text-sm text-blue-700">
                      This certificate is digitally signed and can be verified online.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/training/course/${certificate.id}`)}
                  className="w-full bg-apollo-accent text-white px-4 py-3 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200"
                >
                  View Course Details
                </button>
                
                <button
                  onClick={() => navigate('/training/catalog')}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  Browse More Courses
                </button>
                
                <button
                  onClick={() => navigate('/training/my-training')}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  Back to My Training
                </button>
              </div>
            </div>

            {/* Share Achievement */}
            <div className="bg-gradient-to-br from-apollo-accent to-blue-600 rounded-xl p-6 text-white">
              <h3 className="text-lg font-semibold mb-2">Congratulations! 🎉</h3>
              <p className="text-sm opacity-90 mb-4">
                You've successfully completed {certificate.courseName}. Share your achievement with your professional network!
              </p>
              
              <button
                onClick={shareOnLinkedIn}
                className="w-full bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors duration-200 flex items-center justify-center"
              >
                <ShareIcon className="h-4 w-4 mr-2" />
                Share Achievement
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CertificateViewer;
